import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sun, LogOut, Inbox, Users, Mail, Phone, Building2,
  ChevronDown, CheckCircle2, Clock, Circle, RefreshCw,
  Plus, Trash2, KeyRound, Ban, Play, UserPlus, ExternalLink, Send, Shield,
  Globe, Download, Copy, Server, AlertTriangle, LifeBuoy, UserCog, Rocket, LayoutTemplate, Archive,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import TicketsPanel from "@/components/TicketsPanel";
import SiteEditor from "@/components/SiteEditor";

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-purple-100 text-purple-800 border-purple-200" },
  super_user: { label: "Super User", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  user: { label: "User", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const STATUS_META = {
  new: { label: "New", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Circle },
  contacted: { label: "Contacted", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Clock },
  closed: { label: "Closed", cls: "bg-green-100 text-green-800 border-green-200", Icon: CheckCircle2 },
  converted: { label: "Converted", cls: "bg-purple-100 text-purple-800 border-purple-200", Icon: CheckCircle2 },
  archived: { label: "Archived", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: Circle },
};

const USER_STATUS = {
  active: "bg-green-100 text-green-800 border-green-200",
  invited: "bg-amber-100 text-amber-800 border-amber-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [churches, setChurches] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // Church detail + dialogs
  const [detail, setDetail] = useState(null); // church detail object
  const [detailOpen, setDetailOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newChurchName, setNewChurchName] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", role: "super_user" });
  const [busy, setBusy] = useState(false);
  const [hosting, setHosting] = useState({ hosting_mode: "hosted", external_url: "", hosting_notes: "" });
  const [pubConn, setPubConn] = useState({ method: "manual" });
  const [editorOpen, setEditorOpen] = useState(false);
  // Add-user (global) + lead conversion dialogs
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ church_id: "", name: "", email: "", role: "super_user" });
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [convertForm, setConvertForm] = useState({ church_name: "", contact_name: "", contact_email: "" });

  useEffect(() => {
    if (detail) {
      setHosting({
        hosting_mode: detail.hosting_mode || "hosted",
        external_url: detail.external_url || "",
        hosting_notes: detail.hosting_notes || "",
      });
      setPubConn({ method: (detail.publish && detail.publish.method) || "manual" });
    }
  }, [detail]);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/dashboard");
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, c, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/churches"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setChurches(c.data);
      setAllUsers(u.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (leadId, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
      toast.success(`Marked as ${STATUS_META[status].label}`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      setLeads(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Permanently delete the lead from ${lead.name || lead.email}? This cannot be undone.`)) return;
    const prev = leads;
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead deleted");
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      setLeads(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const openChurch = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/churches/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load church");
    }
  };

  const refreshDetail = async (id) => {
    const { data } = await api.get(`/admin/churches/${id}`);
    setDetail(data);
    const s = await api.get("/admin/stats");
    setStats(s.data);
  };

  const createChurch = async () => {
    if (!newChurchName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/churches", { name: newChurchName.trim() });
      toast.success("Church created");
      setAddOpen(false);
      setNewChurchName("");
      await load();
      openChurch(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create church");
    } finally {
      setBusy(false);
    }
  };

  const inviteUser = async () => {
    if (!invite.name.trim() || !invite.email.trim() || !detail) return;
    setBusy(true);
    try {
      await api.post(`/admin/churches/${detail.id}/invite`, invite);
      toast.success(`Invitation emailed to ${invite.email}`);
      setInvite({ name: "", email: "", role: "super_user" });
      await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not invite user");
    } finally {
      setBusy(false);
    }
  };

  const toggleSuspend = async (u) => {
    const next = u.status === "suspended" ? "active" : "suspended";
    try {
      await api.patch(`/admin/users/${u.id}`, { status: next });
      toast.success(next === "suspended" ? "Access suspended" : "Access restored");
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update user");
    }
  };

  const sendResetLink = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/set-password`, {});
      toast.success(`Password reset link emailed to ${u.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset link");
    }
  };

  const resendInvite = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invitation resent to ${u.email}`);
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend invite");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete user");
    }
  };

  // Change a user's global role (admin-only). `refresh` decides whether to reload church detail too.
  const changeRole = async (u, role, refresh = true) => {
    if (u.role === role) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`${u.name || u.email} is now ${ROLE_META[role]?.label || role}`);
      if (refresh && detail) await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change role");
    }
  };

  const addUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) {
      toast.error("Please fill in name and email.");
      return;
    }
    if (newUser.role !== "admin" && !newUser.church_id) {
      toast.error("Please select a church for this role.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", {
        name: newUser.name.trim(),
        email: newUser.email.trim(),
        role: newUser.role,
        church_id: newUser.role === "admin" ? null : newUser.church_id,
      });
      toast.success(`Invitation emailed to ${newUser.email}`);
      setAddUserOpen(false);
      setNewUser({ church_id: "", name: "", email: "", role: "super_user" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add user");
    } finally {
      setBusy(false);
    }
  };

  const openConvert = (lead) => {
    setConvertLead(lead);
    setConvertForm({
      church_name: lead.ministry_name || lead.name || "",
      contact_name: lead.name || "",
      contact_email: lead.email || "",
    });
    setConvertOpen(true);
  };

  const doConvert = async () => {
    if (!convertForm.church_name.trim() || !convertForm.contact_name.trim() || !convertForm.contact_email.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/leads/${convertLead.id}/convert`, convertForm);
      toast.success(`Account created for ${convertForm.church_name} — invite sent to ${convertForm.contact_email}`);
      setConvertOpen(false);
      setConvertLead(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not convert lead");
    } finally {
      setBusy(false);
    }
  };

  const removeChurch = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete "${detail.name}" and ALL its users and website? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/churches/${detail.id}`);
      toast.success("Church deleted");
      setDetailOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete church");
    }
  };

  const saveHosting = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/churches/${detail.id}/settings`, hosting);
      toast.success("Hosting settings saved");
      await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const markDelivered = async () => {
    try {
      await api.post(`/admin/churches/${detail.id}/mark-delivered`);
      toast.success("Marked as delivered");
      await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const savePubConn = async () => {
    setBusy(true);
    try {
      // Only send non-empty secret fields so we never wipe stored tokens/passwords.
      const payload = { ...pubConn };
      ["github_token", "ftp_password", "azure_deployment_token"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.patch(`/admin/churches/${detail.id}/publish-connection`, payload);
      toast.success("Publishing connection saved");
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save connection");
    } finally {
      setBusy(false);
    }
  };

  const adminPublish = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/churches/${detail.id}/publish`);
      toast.success("Site published");
      await refreshDetail(detail.id);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadExport = async () => {
    try {
      const res = await api.get(`/admin/churches/${detail.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(detail.name || "church-site").toLowerCase().replace(/\s+/g, "-")}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not download export");
    }
  };

  const downloadZip = async () => {
    try {
      const res = await api.get(`/admin/churches/${detail.id}/export/zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(detail.name || "church-site").toLowerCase().replace(/\s+/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP package downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not download ZIP");
    }
  };

  const copyPublicLink = () => copyText(`${window.location.origin}/view/${detail.id}`, "Public site link");

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.message(label, { description: text });
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "Never";
    try {
      return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return "—";
    }
  };

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#F3F4F6]">
        <div className="h-10 w-10 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
      </div>
    );

  const statCards = [
    { label: "New Inquiries", value: stats?.new_leads ?? 0, Icon: Inbox, accent: "text-amber-600 bg-amber-50" },
    { label: "Total Leads", value: stats?.total_leads ?? 0, Icon: Mail, accent: "text-slate-700 bg-slate-100" },
    { label: "Churches", value: stats?.total_churches ?? 0, Icon: Building2, accent: "text-blue-600 bg-blue-50" },
    { label: "Published Sites", value: stats?.published_sites ?? 0, Icon: CheckCircle2, accent: "text-green-600 bg-green-50" },
    { label: "Open Tickets", value: stats?.open_tickets ?? 0, Icon: LifeBuoy, accent: "text-red-600 bg-red-50" },
  ];

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-body flex">
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 text-white p-6 fixed inset-y-0">
        <div>
          <img src="/logo-white.png" alt="Tennessee Web & Design" className="h-11 w-auto" />
        </div>
        <div className="mt-2 text-xs text-amber-400 font-semibold tracking-wide uppercase">Admin Console</div>
        <nav className="mt-10 space-y-1 text-sm">
          <div className="flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2.5 font-medium">
            <Inbox className="h-4 w-4 text-amber-400" /> Leads & Churches
          </div>
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-slate-400">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="admin-logout-btn" className="mt-4 flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors duration-200">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64">
        <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b border-slate-200 px-6 md:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">Admin Dashboard</h1>
            <p className="mt-0.5 text-sm text-slate-500">Manage inquiries, churches, and who can access each account.</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-amber-700 transition-colors duration-200">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={doLogout} data-testid="admin-header-logout-btn" className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-red-600 transition-colors duration-200">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </header>

        <div className="p-6 md:p-8 space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats">
            {statCards.map((s) => (
              <Card key={s.label} className="border-slate-200 shadow-sm">
                <CardContent className="p-5 flex items-center gap-4">
                  <span className={`grid place-items-center h-11 w-11 rounded-xl ${s.accent}`}><s.Icon className="h-5 w-5" /></span>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                    <div className="text-xs text-slate-500">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="leads">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="leads" data-testid="admin-tab-leads"><Inbox className="h-4 w-4 mr-2" />Leads Inbox</TabsTrigger>
              <TabsTrigger value="churches" data-testid="admin-tab-churches"><Building2 className="h-4 w-4 mr-2" />Churches</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-2" />All Users</TabsTrigger>
              <TabsTrigger value="tickets" data-testid="admin-tab-tickets"><LifeBuoy className="h-4 w-4 mr-2" />Tickets</TabsTrigger>
            </TabsList>

            {/* Leads */}
            <TabsContent value="leads">
              {(() => {
                const archivedCount = leads.filter((l) => l.status === "archived").length;
                const visibleLeads = showArchived ? leads : leads.filter((l) => l.status !== "archived");
                return (
                <>
                  {(leads.length > 0) && (
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-slate-500">{visibleLeads.length} lead{visibleLeads.length === 1 ? "" : "s"}{!showArchived && archivedCount > 0 ? ` · ${archivedCount} archived hidden` : ""}</p>
                      {archivedCount > 0 && (
                        <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn" className="text-sm font-medium text-slate-600 hover:text-amber-700 transition-colors duration-200">
                          {showArchived ? "Hide archived" : "Show archived"}
                        </button>
                      )}
                    </div>
                  )}
              {visibleLeads.length === 0 ? (
                <EmptyState icon={Inbox} text={leads.length === 0 ? "No inquiries yet. New leads from the contact form will appear here." : "No active leads. Toggle 'Show archived' to see archived ones."} />
              ) : (
                <div className="space-y-3" data-testid="leads-list">
                  {visibleLeads.map((l) => {
                    const meta = STATUS_META[l.status] || STATUS_META.new;
                    return (
                      <Card key={l.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`lead-row-${l.id}`}>
                        <CardContent className="p-5">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <h3 className="font-semibold text-slate-900">{l.name}</h3>
                                <Badge variant="outline" className={`${meta.cls} border`}>{meta.label}</Badge>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{l.email}</span>
                                {l.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{l.phone}</span>}
                                {l.ministry_name && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{l.ministry_name}</span>}
                              </div>
                              <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{l.message}</p>
                              <div className="mt-2 text-xs text-slate-400">{fmtDate(l.created_at)}</div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button data-testid={`lead-status-trigger-${l.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                                  Status <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {Object.keys(STATUS_META).filter((k) => k !== "converted").map((k) => (
                                  <DropdownMenuItem key={k} data-testid={`lead-status-${k}-${l.id}`} onClick={() => setStatus(l.id, k)}>
                                    {STATUS_META[k].label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            <a href={`mailto:${l.email}`} data-testid={`lead-reply-${l.id}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold px-4 py-2 transition-colors duration-200">
                              <Mail className="h-3.5 w-3.5" /> Reply
                            </a>
                            {l.status === "converted" ? (
                              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700" data-testid={`lead-converted-${l.id}`}>
                                <CheckCircle2 className="h-4 w-4" /> Account created
                              </span>
                            ) : (
                              <button onClick={() => openConvert(l)} data-testid={`lead-convert-${l.id}`} className="inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 transition-colors duration-200">
                                <UserPlus className="h-3.5 w-3.5" /> Convert to Account
                              </button>
                            )}
                            {l.status !== "archived" ? (
                              <button onClick={() => setStatus(l.id, "archived")} data-testid={`lead-archive-${l.id}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold px-4 py-2 transition-colors duration-200">
                                <Archive className="h-3.5 w-3.5" /> Archive
                              </button>
                            ) : (
                              <button onClick={() => setStatus(l.id, "new")} data-testid={`lead-unarchive-${l.id}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold px-4 py-2 transition-colors duration-200">
                                <RefreshCw className="h-3.5 w-3.5" /> Restore
                              </button>
                            )}
                            <button onClick={() => deleteLead(l)} data-testid={`lead-delete-${l.id}`} className="inline-flex items-center gap-2 rounded-full text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 transition-colors duration-200">
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
                </>
                );
              })()}
            </TabsContent>

            {/* Churches */}
            <TabsContent value="churches">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-500">Create church accounts and control who can access each one.</p>
                <Button onClick={() => setAddOpen(true)} data-testid="add-church-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white">
                  <Plus className="h-4 w-4 mr-2" /> Add Church
                </Button>
              </div>
              {churches.length === 0 ? (
                <EmptyState icon={Building2} text="No churches yet. Click 'Add Church' to create your first client account." />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="churches-list">
                  {churches.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => openChurch(c.id)}
                      data-testid={`church-card-${c.id}`}
                      className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                        <Badge variant="outline" className={c.site_status === "published" ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                          {c.site_status === "published" ? "Live" : "Draft"}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={c.hosting_mode === "self_managed" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                          {c.hosting_mode === "self_managed" ? "Self-managed" : "Hosted"}
                        </Badge>
                        {c.pending_delivery && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-1" data-testid={`pending-badge-${c.id}`}>
                            <AlertTriangle className="h-3 w-3" /> Re-deliver
                          </Badge>
                        )}
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                        <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />{c.user_count} {c.user_count === 1 ? "user" : "users"}</span>
                      </div>
                      <div className="mt-2 text-xs text-slate-400">Created {fmtDate(c.created_at)}</div>
                      {c.last_edited_by && (
                        <div className="mt-0.5 text-xs text-slate-400" data-testid={`card-last-edited-${c.id}`}>Last edited by {c.last_edited_by}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* All Users (global RBAC) */}
            <TabsContent value="users">
              <div className="flex items-center justify-between mb-4 gap-3">
                <p className="text-sm text-slate-500">Every account across all churches. Change a user's role to control who can edit sites.</p>
                <Button onClick={() => setAddUserOpen(true)} data-testid="add-user-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white shrink-0">
                  <UserPlus className="h-4 w-4 mr-2" /> Add User
                </Button>
              </div>
              {allUsers.length === 0 ? (
                <EmptyState icon={Users} text="No users yet." />
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="all-users-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-medium">User</th>
                        <th className="px-4 py-3 font-medium hidden sm:table-cell">Church</th>
                        <th className="px-4 py-3 font-medium">Role</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Status</th>
                        <th className="px-4 py-3 font-medium hidden lg:table-cell">Last login</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {allUsers.map((u) => {
                        const rm = ROLE_META[u.role] || ROLE_META.user;
                        return (
                          <tr key={u.id} data-testid={`global-user-row-${u.id}`} className="hover:bg-slate-50/60 transition-colors duration-150">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{u.name || u.email}</div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{u.church_name || "—"}</td>
                            <td className="px-4 py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button data-testid={`global-role-trigger-${u.id}`} className="inline-flex items-center gap-1.5">
                                    <Badge variant="outline" className={`${rm.cls} border cursor-pointer`}>{rm.label}<ChevronDown className="h-3 w-3 ml-1" /></Badge>
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  <DropdownMenuLabel>Set role</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {["admin", "super_user", "user"].map((r) => (
                                    <DropdownMenuItem key={r} data-testid={`global-role-${r}-${u.id}`} onClick={() => changeRole(u, r, false)}>
                                      {ROLE_META[r].label}{u.role === r ? " ✓" : ""}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <Badge variant="outline" className={`${USER_STATUS[u.status] || USER_STATUS.active} border text-xs`}>{u.status}</Badge>
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{fmtDateTime(u.last_login)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* Support Tickets */}
            <TabsContent value="tickets">
              <TicketsPanel isAdmin onChange={load} />
            </TabsContent>
          </Tabs>

          {/* Add Church Dialog */}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogContent data-testid="add-church-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Add a Church</DialogTitle>
                <DialogDescription>Create a new church account. You can invite its team members next.</DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <Label>Church / Ministry Name</Label>
                <Input className="mt-1.5" value={newChurchName} onChange={(e) => setNewChurchName(e.target.value)} data-testid="new-church-name-input" placeholder="Grace Community Church" />
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-md" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={createChurch} disabled={busy} data-testid="create-church-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white">Create Church</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add User (global) Dialog */}
          <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Add a User</DialogTitle>
                <DialogDescription>Provision a new account in a church. They'll get an email to set their password.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs text-slate-500">Role</Label>
                  <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="add-user-role-picker">
                    {[
                      { id: "admin", label: "Admin", desc: "Full admin console" },
                      { id: "super_user", label: "Super User", desc: "Edits a church site" },
                      { id: "user", label: "User", desc: "Read-only access" },
                    ].map((r) => (
                      <button key={r.id} type="button" data-testid={`add-user-role-${r.id}`} onClick={() => setNewUser((v) => ({ ...v, role: r.id }))}
                        className={`rounded-lg border p-2.5 text-left transition-colors duration-200 ${newUser.role === r.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}>
                        <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                        <div className="text-xs text-slate-500">{r.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {newUser.role === "admin" ? (
                  <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-800" data-testid="add-user-admin-note">
                    Admins belong to TN Web Design (not a church) and get full access to this console.
                  </div>
                ) : (
                  <div>
                    <Label>Church</Label>
                    <select
                      value={newUser.church_id}
                      onChange={(e) => setNewUser((v) => ({ ...v, church_id: e.target.value }))}
                      data-testid="add-user-church-select"
                      className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select a church…</option>
                      {churches.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input className="mt-1.5" value={newUser.name} onChange={(e) => setNewUser((v) => ({ ...v, name: e.target.value }))} data-testid="add-user-name-input" placeholder="Full name" /></div>
                  <div><Label>Email</Label><Input type="email" className="mt-1.5" value={newUser.email} onChange={(e) => setNewUser((v) => ({ ...v, email: e.target.value }))} data-testid="add-user-email-input" placeholder="email@church.org" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-md" onClick={() => setAddUserOpen(false)}>Cancel</Button>
                <Button onClick={addUser} disabled={busy} data-testid="submit-add-user-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white"><Send className="h-4 w-4 mr-2" />Send Invite</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Convert Lead Dialog */}
          <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
            <DialogContent data-testid="convert-lead-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Convert Lead to Account</DialogTitle>
                <DialogDescription>Create a church account and invite this contact as its first Super User (they'll manage the site & team).</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Church / Ministry Name</Label><Input className="mt-1.5" value={convertForm.church_name} onChange={(e) => setConvertForm((v) => ({ ...v, church_name: e.target.value }))} data-testid="convert-church-name-input" placeholder="Grace Community Church" /></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Contact Name</Label><Input className="mt-1.5" value={convertForm.contact_name} onChange={(e) => setConvertForm((v) => ({ ...v, contact_name: e.target.value }))} data-testid="convert-contact-name-input" placeholder="Pastor John" /></div>
                  <div><Label>Contact Email</Label><Input type="email" className="mt-1.5" value={convertForm.contact_email} onChange={(e) => setConvertForm((v) => ({ ...v, contact_email: e.target.value }))} data-testid="convert-contact-email-input" placeholder="pastor@church.org" /></div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                  This contact becomes the church's first <b>Super User</b> and receives an invitation email to set their password.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-md" onClick={() => setConvertOpen(false)}>Cancel</Button>
                <Button onClick={doConvert} disabled={busy} data-testid="submit-convert-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white"><UserPlus className="h-4 w-4 mr-2" />Create Account</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Admin site editor (same editor as the client portal) */}
          <Dialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o && detail) { refreshDetail(detail.id); load(); } }}>
            <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="admin-site-editor-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Edit Website — {detail?.name}</DialogTitle>
                <DialogDescription>Edit this church's site content, then Save and Publish — the same editor the church uses.</DialogDescription>
              </DialogHeader>
              {detail && editorOpen && (
                <SiteEditor
                  getSite={async () => (await api.get(`/admin/churches/${detail.id}/site`)).data}
                  saveSite={async (content) => api.put(`/admin/churches/${detail.id}/site`, content)}
                  publishSite={async () => { await api.post(`/admin/churches/${detail.id}/publish`); await refreshDetail(detail.id); }}
                  canEdit
                  exportPrefix={null}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Church Detail Dialog */}
          <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="church-detail-dialog">
              {!detail ? (
                <div className="py-16 grid place-items-center">
                  <DialogTitle className="sr-only">Loading church</DialogTitle>
                  <DialogDescription className="sr-only">Please wait while the church details load.</DialogDescription>
                  <div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
                </div>
              ) : (
                <>
                  <DialogHeader>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <DialogTitle className="font-display text-2xl">{detail.name}</DialogTitle>
                        <DialogDescription className="sr-only">{"Manage this church's website and user access."}</DialogDescription>
                        <Badge variant="outline" className={detail.site_status === "published" ? "bg-green-100 text-green-800 border-green-200 mt-1 inline-flex" : "bg-slate-100 text-slate-600 border-slate-200 mt-1 inline-flex"}>
                          {detail.site_status === "published" ? "Website Live" : "Website Draft"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={copyPublicLink}
                          data-testid="copy-public-link-btn"
                          className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200"
                        >
                          <Copy className="h-4 w-4" /> Copy link
                        </button>
                        <a
                          href={`/view/${detail.id}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="view-website-btn"
                          className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200"
                        >
                          <ExternalLink className="h-4 w-4" /> View Website
                        </a>
                      </div>
                    </div>
                  </DialogHeader>

                  {/* Hosting */}
                  <div className="mt-2 rounded-lg border border-slate-200 p-4" data-testid="hosting-section">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Server className="h-4 w-4" /> Hosting &amp; Publishing</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-slate-200 text-slate-600 text-xs" data-testid="detail-site-type">
                          {detail.site_type === "multi" ? "Multi-page" : "Single-page"}
                        </Badge>
                        <Badge variant="outline" className={`${detail.site_status === "published" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"} text-xs`}>
                          {detail.site_status === "published" ? "Published" : "Draft"}
                        </Badge>
                        <Button size="sm" onClick={adminPublish} disabled={busy} data-testid="admin-publish-btn" className="h-8 rounded-md bg-amber-600 hover:bg-amber-700 text-white"><Rocket className="h-3.5 w-3.5 mr-1.5" />Publish</Button>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setEditorOpen(true)} data-testid="edit-website-btn" className="mb-3 h-8 rounded-md">
                      <LayoutTemplate className="h-3.5 w-3.5 mr-1.5" /> Edit Website
                    </Button>
                    {detail.last_edited_by && (
                      <p className="mb-3 -mt-1 text-xs text-slate-400" data-testid="detail-last-edited">
                        Last edited by {detail.last_edited_by}{detail.last_edited_at ? ` on ${fmtDateTime(detail.last_edited_at)}` : ""}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        data-testid="hosting-mode-hosted"
                        onClick={() => setHosting((h) => ({ ...h, hosting_mode: "hosted" }))}
                        className={`rounded-lg border p-3 text-left transition-colors duration-200 ${hosting.hosting_mode === "hosted" ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        <Globe className={`h-4 w-4 ${hosting.hosting_mode === "hosted" ? "text-amber-600" : "text-slate-400"}`} />
                        <div className="mt-1.5 text-sm font-semibold text-slate-800">Hosted by us</div>
                        <div className="text-xs text-slate-500">Live at a TN Web Design URL</div>
                      </button>
                      <button
                        type="button"
                        data-testid="hosting-mode-self"
                        onClick={() => setHosting((h) => ({ ...h, hosting_mode: "self_managed" }))}
                        className={`rounded-lg border p-3 text-left transition-colors duration-200 ${hosting.hosting_mode === "self_managed" ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        <Server className={`h-4 w-4 ${hosting.hosting_mode === "self_managed" ? "text-amber-600" : "text-slate-400"}`} />
                        <div className="mt-1.5 text-sm font-semibold text-slate-800">Self-managed</div>
                        <div className="text-xs text-slate-500">Client hosts it; edits here</div>
                      </button>
                    </div>

                    {hosting.hosting_mode === "self_managed" && (
                      <div className="mt-4 space-y-3">
                        {detail.pending_delivery && (
                          <div className="flex items-center justify-between rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid="pending-delivery-banner">
                            <span className="text-sm text-amber-800 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Content changed — re-deliver the files</span>
                            <Button size="sm" variant="outline" className="rounded-md h-8" onClick={markDelivered} data-testid="mark-delivered-btn">Mark delivered</Button>
                          </div>
                        )}
                        <div>
                          <Label>External site URL</Label>
                          <Input className="mt-1.5" value={hosting.external_url} onChange={(e) => setHosting((h) => ({ ...h, external_url: e.target.value }))} data-testid="hosting-url-input" placeholder="https://theirchurch.org" />
                        </div>
                        <div>
                          <Label>Delivery / hosting notes</Label>
                          <Input className="mt-1.5" value={hosting.hosting_notes} onChange={(e) => setHosting((h) => ({ ...h, hosting_notes: e.target.value }))} data-testid="hosting-notes-input" placeholder="e.g. Hosted on their GoDaddy; push to their GitHub repo X" />
                        </div>

                        {/* Publishing connection */}
                        <div className="rounded-lg border border-slate-200 p-4" data-testid="connection-section">
                          <h5 className="text-sm font-semibold text-slate-700 mb-1">Publishing connection</h5>
                          <p className="text-xs text-slate-500 mb-3">How updated files reach the church's host. Live auto-publish for GitHub/FTP/Azure is coming soon — settings are stored now.</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="connection-method-picker">
                            {[
                              { id: "manual", label: "Manual" },
                              { id: "github", label: "GitHub" },
                              { id: "ftp", label: "FTP / SFTP" },
                              { id: "azure", label: "Azure" },
                            ].map((m) => (
                              <button key={m.id} type="button" data-testid={`connection-method-${m.id}`} onClick={() => setPubConn((p) => ({ ...p, method: m.id }))}
                                className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors duration-200 ${pubConn.method === m.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500 text-amber-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                                {m.label}
                              </button>
                            ))}
                          </div>

                          {pubConn.method === "manual" && (
                            <p className="mt-3 text-xs text-slate-500">Download the files below and hand them to the church, or they self-serve from their dashboard.</p>
                          )}

                          {pubConn.method === "github" && (
                            <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="connection-github">
                              <Input className="text-sm" value={pubConn.github_repo || ""} onChange={(e) => setPubConn((p) => ({ ...p, github_repo: e.target.value }))} data-testid="gh-repo" placeholder="owner/repo" />
                              <Input className="text-sm" value={pubConn.github_branch || ""} onChange={(e) => setPubConn((p) => ({ ...p, github_branch: e.target.value }))} data-testid="gh-branch" placeholder="branch (e.g. main)" />
                              <Input className="text-sm" value={pubConn.github_path || ""} onChange={(e) => setPubConn((p) => ({ ...p, github_path: e.target.value }))} data-testid="gh-path" placeholder="path (e.g. /)" />
                              <Input type="password" className="text-sm" value={pubConn.github_token || ""} onChange={(e) => setPubConn((p) => ({ ...p, github_token: e.target.value }))} data-testid="gh-token" placeholder={detail.publish?.github_token_set ? "•••••• (saved — leave blank to keep)" : "personal access token"} />
                            </div>
                          )}

                          {pubConn.method === "ftp" && (
                            <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="connection-ftp">
                              <select value={pubConn.ftp_protocol || "ftp"} onChange={(e) => setPubConn((p) => ({ ...p, ftp_protocol: e.target.value }))} data-testid="ftp-protocol" className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                                <option value="ftp">FTP</option>
                                <option value="sftp">SFTP</option>
                              </select>
                              <Input className="text-sm" value={pubConn.ftp_host || ""} onChange={(e) => setPubConn((p) => ({ ...p, ftp_host: e.target.value }))} data-testid="ftp-host" placeholder="ftp.host.com" />
                              <Input className="text-sm" value={pubConn.ftp_port || ""} onChange={(e) => setPubConn((p) => ({ ...p, ftp_port: e.target.value }))} data-testid="ftp-port" placeholder="port (21 / 22)" />
                              <Input className="text-sm" value={pubConn.ftp_username || ""} onChange={(e) => setPubConn((p) => ({ ...p, ftp_username: e.target.value }))} data-testid="ftp-username" placeholder="username" />
                              <Input type="password" className="text-sm" value={pubConn.ftp_password || ""} onChange={(e) => setPubConn((p) => ({ ...p, ftp_password: e.target.value }))} data-testid="ftp-password" placeholder={detail.publish?.ftp_password_set ? "•••••• (saved)" : "password"} />
                              <Input className="text-sm" value={pubConn.ftp_remote_dir || ""} onChange={(e) => setPubConn((p) => ({ ...p, ftp_remote_dir: e.target.value }))} data-testid="ftp-remote-dir" placeholder="remote dir (e.g. /public_html)" />
                            </div>
                          )}

                          {pubConn.method === "azure" && (
                            <div className="mt-3" data-testid="connection-azure">
                              <Input type="password" className="text-sm" value={pubConn.azure_deployment_token || ""} onChange={(e) => setPubConn((p) => ({ ...p, azure_deployment_token: e.target.value }))} data-testid="azure-token" placeholder={detail.publish?.azure_token_set ? "•••••• (saved)" : "Azure Static Web Apps deployment token"} />
                            </div>
                          )}

                          <Button size="sm" onClick={savePubConn} disabled={busy} data-testid="save-connection-btn" className="mt-3 rounded-md bg-slate-800 hover:bg-slate-900 text-white h-8">Save connection</Button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" className="rounded-md" onClick={downloadZip} data-testid="download-zip-btn"><Download className="h-4 w-4 mr-2" />Download ZIP</Button>
                          <Button variant="outline" className="rounded-md" onClick={downloadExport} data-testid="download-export-btn"><Download className="h-4 w-4 mr-2" />HTML only</Button>
                          <Button variant="outline" className="rounded-md" onClick={() => copyText(`<iframe src="${window.location.origin}/view/${detail.id}" style="width:100%;height:100vh;border:0" title="${detail.name}"></iframe>`, "Embed snippet")} data-testid="copy-embed-btn"><Copy className="h-4 w-4 mr-2" />Copy embed</Button>
                          <Button variant="outline" className="rounded-md" onClick={() => copyText(`${process.env.REACT_APP_BACKEND_URL}/api/public/feed/${detail.id}`, "Data feed URL")} data-testid="copy-feed-btn"><Copy className="h-4 w-4 mr-2" />Copy data feed</Button>
                        </div>
                      </div>
                    )}

                    <Button onClick={saveHosting} disabled={busy} data-testid="save-hosting-btn" className="mt-4 rounded-md bg-amber-600 hover:bg-amber-700 text-white">Save Hosting Settings</Button>
                  </div>

                  {/* Users */}
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Users className="h-4 w-4" /> Who can access</h4>
                    <div className="space-y-2" data-testid="church-users-list">
                      {detail.users.length === 0 && <p className="text-sm text-slate-400">No users yet. Invite someone below.</p>}
                      {detail.users.map((u) => (
                        <div key={u.id} className="rounded-lg border border-slate-200 p-3" data-testid={`user-row-${u.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-900">{u.name || u.email}</span>
                                {u.role === "admin" && <Shield className="h-3.5 w-3.5 text-purple-600" />}
                                <Badge variant="outline" className={`${(ROLE_META[u.role] || ROLE_META.user).cls} border text-xs`} data-testid={`user-role-badge-${u.id}`}>{(ROLE_META[u.role] || ROLE_META.user).label}</Badge>
                                <Badge variant="outline" className={`${USER_STATUS[u.status] || USER_STATUS.active} border text-xs`}>{u.status}</Badge>
                              </div>
                              <div className="text-sm text-slate-500">{u.email}</div>
                              <div className="text-xs text-slate-400 mt-0.5">Last login: {fmtDateTime(u.last_login)}</div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button data-testid={`user-actions-${u.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                                  Manage <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger data-testid={`user-role-submenu-${u.id}`}>
                                    <UserCog className="h-4 w-4 mr-2" />Change role
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    {["super_user", "user"].map((r) => (
                                      <DropdownMenuItem key={r} data-testid={`user-role-${r}-${u.id}`} onClick={() => changeRole(u, r)}>
                                        {ROLE_META[r].label}{u.role === r ? " ✓" : ""}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => toggleSuspend(u)} data-testid={`user-suspend-${u.id}`}>
                                  {u.status === "suspended" ? <><Play className="h-4 w-4 mr-2" />Restore access</> : <><Ban className="h-4 w-4 mr-2" />Suspend access</>}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendResetLink(u)} data-testid={`user-reset-${u.id}`}>
                                  <KeyRound className="h-4 w-4 mr-2" />Email password reset
                                </DropdownMenuItem>
                                {u.status === "invited" && (
                                  <DropdownMenuItem onClick={() => resendInvite(u)} data-testid={`user-resend-${u.id}`}>
                                    <Send className="h-4 w-4 mr-2" />Resend invite
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`} className="text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />Delete user
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Invite */}
                  <div className="mt-4 rounded-lg bg-slate-50 p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Invite a team member</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input value={invite.name} onChange={(e) => setInvite((v) => ({ ...v, name: e.target.value }))} data-testid="invite-name-input" placeholder="Name" />
                      <Input type="email" value={invite.email} onChange={(e) => setInvite((v) => ({ ...v, email: e.target.value }))} data-testid="invite-email-input" placeholder="email@church.org" />
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs text-slate-500">Role</Label>
                      <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="invite-role-picker">
                        {[
                          { id: "super_user", label: "Super User", desc: "Can edit the website" },
                          { id: "user", label: "User", desc: "Read-only access" },
                        ].map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            data-testid={`invite-role-${r.id}`}
                            onClick={() => setInvite((v) => ({ ...v, role: r.id }))}
                            className={`rounded-lg border p-2.5 text-left transition-colors duration-200 ${invite.role === r.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}
                          >
                            <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                            <div className="text-xs text-slate-500">{r.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button onClick={inviteUser} disabled={busy} data-testid="invite-user-btn" className="mt-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white">
                      <Send className="h-4 w-4 mr-2" /> Send Invite
                    </Button>
                  </div>

                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={removeChurch} data-testid="delete-church-btn" className="rounded-md text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="h-4 w-4 mr-2" /> Delete Church
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="empty-state">
      <Icon className="h-10 w-10 text-slate-300 mx-auto" />
      <p className="mt-4 text-slate-500 max-w-sm mx-auto">{text}</p>
    </div>
  );
}
